import React, { useMemo } from "react";
import {
	Avatar,
	Button,
	Chip,
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	Tab,
	Tabs,
} from "@heroui/react";
import { AnimatePresence, motion } from "framer-motion";
import BoxTab from "./BoxTab";
import { useTheme } from "next-themes";

function GuardTab({ guards }) {
	const { theme } = useTheme();
	const isDark = theme === "dark";
	const formatDate = (dateStr) => {
		if (!dateStr) return "-";
		const d = new Date(dateStr);
		if (isNaN(d.getTime())) return "-";
		return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
	};

	const getMedalColor = (level) => {
		if (
			typeof window !== "undefined" &&
			typeof window.getColor === "function"
		) {
			return window.getColor(level);
		}
		if (level <= 10) return "#727BB5";
		if (level <= 20) return "#CF86B2";
		if (level <= 30) return "#5EC0F7";
		if (level <= 40) return "#6992FF";
		if (level <= 50) return "#AA78F1";
		if (level <= 60) return "#ED5674";
		if (level <= 70) return "#F58737";
		if (level <= 80) return "#F58837";
		if (level <= 90) return "#F58837";
		return "#FF9D55";
	};
	const [expandedGuards, setExpandedGuards] = React.useState(new Set());

	const mergedGuards = useMemo(() => {
		if (!guards || guards.length === 0) return [];

		const map = new Map();
		guards.forEach((item) => {
			const key = `${item.target_id}_${item.type}`;
			if (!map.has(key)) {
				map.set(key, []);
			}
			map.get(key).push(item);
		});

		const result = [];
		map.forEach((records, key) => {
			// 按更新时间 / 到期时间从新到旧排序
			records.sort((a, b) => {
				const timeA = new Date(a.updated_at || a.expired_at).getTime();
				const timeB = new Date(b.updated_at || b.expired_at).getTime();
				return timeB - timeA;
			});

			// 对相同更新时间和到期时间的快照去重（不同抓取批次可能产生重复行）
			const seen = new Set();
			const uniqueRecords = [];
			for (const r of records) {
				const dedupeKey = `${formatDate(r.updated_at)}_${formatDate(r.expired_at)}`;
				if (!seen.has(dedupeKey)) {
					seen.add(dedupeKey);
					uniqueRecords.push(r);
				}
			}

			const latest = uniqueRecords[0];
			// 首次上舰时间取最早的一条
			const earliestCreated = uniqueRecords.reduce((min, cur) => {
				return new Date(cur.created_at) < new Date(min)
					? cur.created_at
					: min;
			}, latest.created_at);

			// 粉丝牌信息
			const medalName =
				latest.medal_name ||
				uniqueRecords.find((r) => r.medal_name)?.medal_name;
			const level =
				latest.level || uniqueRecords.find((r) => r.level)?.level;

			result.push({
				...latest,
				created_at: earliestCreated,
				history: uniqueRecords,
				medal_name: medalName,
				level: level,
				key,
			});
		});

		result.sort((a, b) => {
			if (a.type !== b.type) return a.type - b.type;
			return (
				new Date(b.expired_at).getTime() -
				new Date(a.expired_at).getTime()
			);
		});

		return result;
	}, [guards]);
	return (
		<div>
			<div className="flex flex-col gap-3 py-1">
				<p className={"text-gray text-[14px]"}>
					从直播弹幕里提取出来的，可能会不完整或者有偏差
				</p>
				{mergedGuards.map((item) => {
					const isExpired =
						new Date(item.expired_at).getTime() < Date.now();
					const themeColor =
						["#f58220", "#5a1db5", "#4f7df3"][item.type - 1] ||
						"#4f7df3";
					const tierName =
						["总督", "提督", "舰长"][item.type - 1] || "舰长";

					const gray1 = isDark ? "#2d2d32" : "#a2a4a6";
					const gray2 = isDark ? "#242429" : "#e2e6ec";
					const gray3 = isDark ? "#1c1c20" : "#eef1f5";

					// 正常状态用原版右侧光晕渐变；过期状态使用带身份微色调的平缓灰阶渐变（保留金/紫/蓝专属底色）
					const gradientStyle = {
						"--theme": themeColor,
						background: isExpired
							? `linear-gradient(
													278deg,
													color-mix(in srgb, var(--theme) ${isDark ? "22%" : "18%"}, ${gray1}) 0%,
													color-mix(in srgb, var(--theme) ${isDark ? "10%" : "8%"}, ${gray2}) 40%,
													${gray3} 85%
												)`
							: `linear-gradient(
													278deg,
													color-mix(in srgb, var(--theme) 38%, transparent) 0%,
													color-mix(in srgb, var(--theme) 32%, transparent) 22%,
													color-mix(in srgb, var(--theme) 20%, transparent) 40%,
													color-mix(in srgb, var(--theme) 8%, transparent) 60%,
													color-mix(in srgb, var(--theme) 2%, transparent) 76%,
													transparent 90%
												)`,
					};
					const isExpanded = expandedGuards.has(item.key);

					return (
						<motion.div
							layout
							key={item.key}
							className={`w-full rounded-xl border shadow-sm flex flex-col p-3 transition-all ${
								isExpired
									? "border-zinc-300/80 dark:border-zinc-700/80 opacity-90 hover:opacity-100"
									: "border-black/5 dark:border-white/10 hover:shadow-md"
							}`}
							style={gradientStyle}
						>
							<div className="w-full flex items-center justify-between">
								<div className="flex-1 min-w-0 flex flex-col gap-2">
									<div className="flex items-center justify-between pr-2 min-w-0">
										<div className="flex items-center gap-2.5 min-w-0">
											<Avatar
												src={`${AVATAR_API}${item.target_id}`}
												className="w-10 h-10 shrink-0 ring-1 ring-black/10 dark:ring-white/10 cursor-pointer"
												onClick={() =>
													typeof toSpace ===
													"function"
														? toSpace(
																item.target_id,
															)
														: window.toSpace &&
															window.toSpace(
																item.target_id,
															)
												}
											/>
											<div className="flex flex-col items-start gap-1 min-w-0">
												<span
													className="font-bold text-sm sm:text-base text-zinc-900 dark:text-zinc-100 truncate cursor-pointer hover:underline"
													title={item.u_name}
													onClick={() =>
														typeof toSpace ===
														"function"
															? toSpace(
																	item.target_id,
																)
															: window.toSpace &&
																window.toSpace(
																	item.target_id,
																)
													}
												>
													{item.u_name}
												</span>
												<div className="flex items-center gap-1.5 flex-wrap">
													{item.medal_name && (
														<Chip
															size="sm"
															variant="faded"
															style={{
																color: "white",
																backgroundColor:
																	getMedalColor(
																		item.level,
																	),
															}}
															className="h-5 px-1.5 text-[11px] font-medium select-none shrink-0 border-0"
														>
															<span>
																{
																	item.medal_name
																}
															</span>
															<span className="ml-1 text-[10px] font-bold px-1.5 py-0.2 rounded-full bg-white/20">
																{item.level}
															</span>
														</Chip>
													)}
												</div>
											</div>
										</div>

										{item.history &&
											item.history.length > 1 && (
												<button
													type="button"
													onClick={(e) => {
														e.stopPropagation();
														setExpandedGuards(
															(prev) => {
																const next =
																	new Set(
																		prev,
																	);
																if (
																	next.has(
																		item.key,
																	)
																) {
																	next.delete(
																		item.key,
																	);
																} else {
																	next.add(
																		item.key,
																	);
																}
																return next;
															},
														);
													}}
													className="flex items-center gap-1 text-[11px] font-medium text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors cursor-pointer select-none py-0.5 px-2 rounded-full bg-black/5 dark:bg-white/10 hover:bg-black/10 shrink-0 self-start mt-0.5"
												>
													<span>
														{item.history.length}{" "}
														次记录
													</span>
													<motion.span
														animate={{
															rotate: isExpanded
																? 180
																: 0,
														}}
														transition={{
															duration: 0.2,
														}}
														className="inline-block text-[9px]"
													>
														▼
													</motion.span>
												</button>
											)}
									</div>
									<div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px] sm:text-xs">
										<div className="flex items-center whitespace-nowrap">
											<span className="text-zinc-500 dark:text-zinc-400 mr-1.5 shrink-0">
												首次上舰
											</span>
											<span className=" text-zinc-800 dark:text-zinc-200">
												{formatDate(item.created_at)}
											</span>
										</div>
										<div className="flex items-center whitespace-nowrap">
											<span className="text-zinc-500 dark:text-zinc-400 mr-1.5 shrink-0">
												本周期开始
											</span>
											<span className=" text-zinc-800 dark:text-zinc-200">
												{formatDate(
													item.cycle_created_at,
												)}
											</span>
										</div>
										<div className="flex items-center whitespace-nowrap">
											<span className="text-zinc-500 dark:text-zinc-400 mr-1.5 shrink-0">
												过期时间
											</span>
											<span
												className={`font-semibold ${isExpired ? "text-zinc-600 dark:text-zinc-400" : "text-zinc-800 dark:text-zinc-200"}`}
											>
												{formatDate(item.expired_at)}
												{isExpired && (
													<span className="ml-1.5 text-[10px] font-normal px-1.5 py-0.5 rounded bg-zinc-400/20 text-zinc-600 dark:text-zinc-300">
														已过期
													</span>
												)}
											</span>
										</div>
										<div className="flex items-center whitespace-nowrap">
											<span className="text-zinc-500 dark:text-zinc-400 mr-1.5 shrink-0">
												上次变更
											</span>
											<span className=" text-zinc-800 dark:text-zinc-200">
												{formatDate(item.updated_at)}
											</span>
										</div>
									</div>
								</div>
								<div className="shrink-0 ml-2">
									<img
										className={`w-14 h-14 sm:w-16 sm:h-16 object-contain select-none transition-all ${
											isExpired
												? "opacity-85 drop-shadow-none"
												: "drop-shadow-sm"
										}`}
										src={
											[
												"https://i0.hdslb.com/bfs/live/44802c767464421648438583a5bb0ce336fe1b3d.png",
												"https://i0.hdslb.com/bfs/live/ea985665bfdc4b0ce894b8fcf7c29fecc3136a08.png",
												"https://i0.hdslb.com/bfs/live/f1be2a2d5b227ce72641de1ad64bcc7f9e4111c3.png",
											][item.type - 1]
										}
										alt={tierName}
									/>
								</div>
							</div>

							<AnimatePresence initial={false}>
								{isExpanded &&
									item.history &&
									item.history.length > 1 && (
										<motion.div
											key="history-content"
											initial={{
												opacity: 0,
												height: 0,
											}}
											animate={{
												opacity: 1,
												height: "auto",
											}}
											exit={{
												opacity: 0,
												height: 0,
											}}
											transition={{
												duration: 0.25,
												ease: [0.04, 0.62, 0.23, 0.98],
											}}
											className="overflow-hidden"
										>
											<div className="mt-2.5 pt-2 border-t border-black/5 dark:border-white/10 flex flex-col gap-1 text-[11px]">
												<div className="text-[10px] text-zinc-500 dark:text-zinc-400 font-medium">
													历史续费与变更明细：
												</div>
												<div className="flex flex-col gap-1">
													{item.history.map(
														(h, idx) => (
															<div
																key={`${h.updated_at || h.expired_at}-${idx}`}
																className={`flex items-center justify-between py-1 px-2 rounded-lg text-[11px] ${
																	idx === 0
																		? "bg-black/5 dark:bg-white/5 font-medium text-zinc-800 dark:text-zinc-200"
																		: "text-zinc-500 dark:text-zinc-400"
																}`}
															>
																<div className="flex items-center gap-1.5">
																	<span className="text-[9px] px-1 py-0.2 rounded bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
																		{idx ===
																		0
																			? "最新"
																			: `#${item.history.length - idx}`}
																	</span>
																	<span>
																		变更:{" "}
																		{formatDate(
																			h.updated_at,
																		)}
																	</span>
																</div>
																<div>
																	到期:{" "}
																	{formatDate(
																		h.expired_at,
																	)}
																</div>
															</div>
														),
													)}
												</div>
											</div>
										</motion.div>
									)}
							</AnimatePresence>
						</motion.div>
					);
				})}
			</div>
		</div>
	);
}

export default GuardTab;
